import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import api, { API } from "@/lib/api";
import {
  LogOut, Users, Clock, Trophy, Gamepad2, Search, X,
  Bot, ShieldCheck, AlertTriangle, History, Calendar, ChevronDown, ChevronRight,
  Download, Share2, Video,
} from "lucide-react";
import WinnerCard from "@/components/WinnerCard";

const formatDuration = (secs) => {
  if (!secs) return "0s";
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}m ${s}s`;
};

const formatTs = (ts) => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

const verdictColor = (v) => {
  if (v === "LIKELY_BOT") return "#FF007F";
  if (v === "LIKELY_HUMAN") return "#39FF14";
  if (v === "SUSPICIOUS") return "#EAEB18";
  return "#94A3B8";
};

const verdictIcon = (v) => {
  if (v === "LIKELY_BOT") return Bot;
  if (v === "LIKELY_HUMAN") return ShieldCheck;
  return AlertTriangle;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [top3, setTop3] = useState([]);
  const [period, setPeriod] = useState("daily");

  const [search, setSearch] = useState("");

  // Player-detail modal state
  const [playerModal, setPlayerModal] = useState(null); // {user_id,...}
  const [playerLoading, setPlayerLoading] = useState(false);

  // Per-game bot-detection review modal
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Reports (last 7 days daily + last 4 weeks weekly)
  const [dailyReports, setDailyReports] = useState([]);
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [reportsPlan, setReportsPlan] = useState("global"); // "global" | "darepass"
  const [reportMode, setReportMode] = useState("daily"); // 'daily' | 'weekly'
  const [expandedReport, setExpandedReport] = useState(null); // date/week_start key

  // Winner cards
  const [winnerCards, setWinnerCards] = useState([]); // [{date, day_label, winner, prize_inr}]
  const [activeWinner, setActiveWinner] = useState(null); // {winner, date, dayLabel}

  // Ad impressions
  const [adStats, setAdStats] = useState(null); // {today, total, daily}
  const [rooms, setRooms] = useState([]); // admin rooms list
  const [roomsDaily, setRoomsDaily] = useState([]); // last 7 days rooms count
  const [roomsFilter, setRoomsFilter] = useState("all"); // all | active | expired

  const openPlayer = async (user_id) => {
    setPlayerLoading(true);
    setPlayerModal({ user_id, loading: true });
    try {
      const r = await api.get(`/admin/players/${user_id}/games?limit=50`);
      setPlayerModal(r.data);
    } catch {
      setPlayerModal({ error: "Failed to load player history" });
    } finally {
      setPlayerLoading(false);
    }
  };
  const closePlayer = () => setPlayerModal(null);

  const openReview = async (gameId) => {
    setReviewLoading(true);
    setReviewData({ loading: true });
    try {
      const r = await api.get(`/admin/games/${gameId}/review`);
      setReviewData(r.data);
    } catch {
      setReviewData({ error: "Failed to load review" });
    } finally {
      setReviewLoading(false);
    }
  };
  const closeReview = () => setReviewData(null);

  // Excel export for admin reports (daily / weekly)
  const downloadReportsExcel = (mode) => {
    const reports = mode === "daily" ? dailyReports : weeklyReports;
    if (!reports.length) return;
    const wb = XLSX.utils.book_new();
    // Sheet 1: summary
    const summary = reports.map((r) => ({
      [mode === "daily" ? "Date" : "Week"]: r.day_label || r.week_label,
      Players: r.player_count,
      "Top #1 Name": r.top?.[0]?.name || "—",
      "Top #1 Score": r.top?.[0]?.score ?? 0,
      "Top #1 UPI": r.top?.[0]?.upi || "—",
    }));
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(summary),
      "Summary",
    );
    // Individual sheets per day / week
    reports.forEach((r) => {
      const rows = (r.top || []).map((row) => ({
        Rank: row.rank,
        Name: row.name,
        Email: row.email || "",
        UPI: row.upi || "",
        Score: row.score,
        Timestamp: row.timestamp,
      }));
      // Sheet name max 31 chars
      const safeName = (r.date || r.week_start || "sheet")
        .replace(/[\\/*?:[\]]/g, "-")
        .slice(0, 31);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows.length ? rows : [{ Info: "No games this period" }]),
        safeName,
      );
    });
    const stamp = new Date().toISOString().slice(0, 10);
    const fname =
      mode === "daily"
        ? `DareDino-daily-reports-last7days-${stamp}.xlsx`
        : `DareDino-weekly-reports-last4weeks-${stamp}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  useEffect(() => {
    document.title = "DareDino · Admin Terminal";
    let cancelled = false;
    const load = async () => {
      try {
        await api.get("/admin/check");
        const [s, p, g, t, rd, rw, wn, ads, rms, rmsD] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/players"),
          api.get("/admin/games"),
          api.get(`/admin/top3?period=${period}&plan=${reportsPlan}`),
          api.get(`/admin/reports/daily?days=7&top=10&plan=${reportsPlan}`),
          api.get(`/admin/reports/weekly?weeks=4&top=10&plan=${reportsPlan}`),
          api.get(`/winners/daily?days=7&plan=${reportsPlan}`),
          api.get("/admin/ad-stats?days=7"),
          api.get(`/admin/rooms?limit=100&status=${roomsFilter}`),
          api.get("/admin/rooms/daily?days=7"),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setPlayers(p.data.players || []);
        setGames(g.data.games || []);
        setTop3(t.data.top3 || []);
        setDailyReports(rd.data.reports || []);
        setWeeklyReports(rw.data.reports || []);
        setWinnerCards(wn.data.winners || []);
        setAdStats(ads.data);
        setRooms(rms.data.rooms || []);
        setRoomsDaily(rmsD.data.days || []);
      } catch {
        navigate("/", { replace: true });
      }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [navigate, period, roomsFilter, reportsPlan]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    navigate("/", { replace: true });
  };

  const q = search.trim().toLowerCase();

  const filteredPlayers = useMemo(() => {
    if (!q) return players;
    return players.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.upi || "").toLowerCase().includes(q),
    );
  }, [players, q]);

  const filteredGames = useMemo(() => {
    if (!q) return games;
    return games.filter(
      (g) =>
        (g.user_name || "").toLowerCase().includes(q) ||
        String(g.score).includes(q),
    );
  }, [games, q]);

  return (
    <div className="min-h-screen bg-[#050505] relative">
      <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-4 md:px-8 py-4 border-b border-[#FF007F]/30 bg-[#0B0C10]/60 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-[#FF007F] flex items-center justify-center text-black font-black">
            A
          </div>
          <div className="font-heading font-black text-lg tracking-wider text-white">
            DAREDINO <span className="text-[#FF007F]">/ ADMIN</span>
          </div>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] hover:text-[#FF007F] transition-colors flex items-center gap-2"
        >
          <LogOut size={14} strokeWidth={2.5} />
          Logout
        </button>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Search bar */}
        <div className="mb-8 flex items-center gap-3 border border-[#00F0FF]/40 bg-[#0B0C10] px-4 py-3">
          <Search size={16} strokeWidth={2.5} className="text-[#00F0FF]" />
          <input
            data-testid="admin-search-input"
            type="text"
            placeholder="Search by player name, email, UPI, or score…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-[#475569] font-mono-arcade"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              data-testid="admin-search-clear"
              className="text-[#FF007F] hover:text-[#EAEB18]"
              aria-label="Clear search"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Total Players" value={stats?.total_users ?? "—"} color="#00F0FF" />
          <StatCard icon={Gamepad2} label="Total Games" value={stats?.total_games ?? "—"} color="#EAEB18" />
          <StatCard icon={Clock} label="Avg Session" value={stats ? formatDuration(stats.avg_session_seconds) : "—"} color="#39FF14" />
          <StatCard icon={Clock} label="Avg Time / User" value={stats ? formatDuration(stats.avg_time_per_user_seconds) : "—"} color="#FF007F" />
        </div>

        {/* AD IMPRESSIONS (resume-with-ad tracking) */}
        <section className="mb-8 border border-[#39FF14]/40 bg-[#0B0C10]" data-testid="admin-ad-impressions">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#39FF14]/30 px-4 py-3">
            <div className="flex items-center gap-2">
              <Video size={16} strokeWidth={2.5} className="text-[#39FF14]" />
              <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                Ad Impressions
              </div>
              <span className="text-[9px] uppercase tracking-[0.2em] text-[#94A3B8]">
                · rewarded ads · completed only · all sources
              </span>
            </div>
          </div>

          {/* Breakdown by source — user asked what "Ad Impressions" counts. */}
          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
            Last 7 days by source
          </div>
          <div className="px-4 pb-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ad-by-context">
            {[
              { key: "game_resume", label: "Main game resume", color: "#39FF14" },
              { key: "room_resume", label: "Friends room resume", color: "#EAEB18" },
              { key: "room_create", label: "Free room create", color: "#00F0FF" },
              { key: "legacy", label: "Legacy (untagged)", color: "#94A3B8" },
            ].map((c) => (
              <div
                key={c.key}
                className="border border-white/10 bg-[#050505] px-3 py-2"
                data-testid={`ad-ctx-${c.key}`}
              >
                <div className="text-[9px] uppercase tracking-[0.25em] text-[#94A3B8]">
                  {c.label}
                </div>
                <div className="font-heading font-black text-xl mt-0.5" style={{ color: c.color }}>
                  {adStats?.by_context?.[c.key] ?? "—"}
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard
              icon={Video}
              label="Today · Impressions"
              value={adStats?.today?.impressions ?? "—"}
              color="#39FF14"
              testid="ad-today-impressions"
            />
            <StatCard
              icon={Users}
              label="Today · Unique Viewers"
              value={adStats?.today?.unique_viewers ?? "—"}
              color="#00F0FF"
              testid="ad-today-viewers"
            />
            <StatCard
              icon={ShieldCheck}
              label="Today · Avg / User"
              value={adStats?.today?.avg_per_viewer ?? "—"}
              color="#EAEB18"
              testid="ad-today-avg"
            />
            <StatCard
              icon={Video}
              label="Last 7d · Total"
              value={adStats?.total?.impressions ?? "—"}
              color="#39FF14"
              testid="ad-total-impressions"
            />
            <StatCard
              icon={Users}
              label="Last 7d · Unique"
              value={adStats?.total?.unique_viewers ?? "—"}
              color="#00F0FF"
              testid="ad-total-viewers"
            />
            <StatCard
              icon={ShieldCheck}
              label="Last 7d · Avg / User"
              value={adStats?.total?.avg_per_viewer ?? "—"}
              color="#EAEB18"
              testid="ad-total-avg"
            />
          </div>

          <div className="border-t border-[#39FF14]/20 overflow-x-auto">
            <table className="w-full text-xs font-mono-arcade" data-testid="ad-daily-table">
              <thead>
                <tr className="border-b border-[#39FF14]/20 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                  <th className="text-left p-3">Day</th>
                  <th className="text-right p-3">Impressions</th>
                  <th className="text-right p-3">Unique viewers</th>
                  <th className="text-right p-3">Avg / viewer</th>
                </tr>
              </thead>
              <tbody>
                {(!adStats || adStats.daily.length === 0) && (
                  <tr>
                    <td colSpan={4} className="text-center p-4 text-[#94A3B8]">
                      No ad views yet.
                    </td>
                  </tr>
                )}
                {adStats?.daily.map((d) => (
                  <tr key={d.date} className="border-b border-[#39FF14]/10 hover:bg-[#39FF14]/5">
                    <td className="p-3 text-white">{d.day_label}</td>
                    <td className="p-3 text-right text-[#39FF14] font-bold">{d.impressions}</td>
                    <td className="p-3 text-right text-[#00F0FF]">{d.unique_viewers}</td>
                    <td className="p-3 text-right text-[#EAEB18]">{d.avg_per_viewer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ROOMS (Play With Friends) */}
        <section className="mb-8 border border-[#00F0FF]/50 bg-[#0B0C10]" data-testid="admin-rooms-section">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#00F0FF]/30 px-4 py-3">
            <div className="flex items-center gap-2">
              <Users size={16} strokeWidth={2.5} className="text-[#00F0FF]" />
              <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                Rooms · Play With Friends
              </div>
              <span className="text-[9px] uppercase tracking-[0.2em] text-[#94A3B8]" />
            </div>
            <div className="flex gap-1" data-testid="admin-rooms-filter">
              {["all", "active", "expired"].map((f) => (
                <button
                  key={f}
                  onClick={() => setRoomsFilter(f)}
                  data-testid={`admin-rooms-filter-${f}`}
                  className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
                    roomsFilter === f
                      ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10"
                      : "border-[#00F0FF]/30 text-[#94A3B8] hover:text-[#00F0FF]"
                  }`}
                >
                  {f}
                </button>
              ))}
              <a
                href={`${API}/admin/export/rooms.csv`}
                data-testid="download-rooms-csv"
                className="text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10"
                title="Download all rooms as CSV"
              >
                ⇩ CSV
              </a>
            </div>
          </div>

          {/* Room stat tiles */}
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={Users}
              label="Active Rooms"
              value={stats?.rooms?.active_now ?? "—"}
              color="#00F0FF"
              testid="rooms-active-count"
            />
            <StatCard
              icon={Gamepad2}
              label="Created Today (IST)"
              value={stats?.rooms?.created_today_ist ?? "—"}
              color="#EAEB18"
              testid="rooms-today-count"
            />
            <StatCard
              icon={Clock}
              label="Created This Week"
              value={stats?.rooms?.created_this_week_ist ?? "—"}
              color="#39FF14"
              testid="rooms-week-count"
            />
            <StatCard
              icon={Video}
              label="Room-create Ad Views (Today)"
              value={stats?.rooms?.ad_views_room_create_today ?? "—"}
              color="#FF007F"
              testid="rooms-ads-today"
            />
          </div>

          {/* Daily rooms bar chart */}
          {roomsDaily.length > 0 && (
            <div className="px-4 pb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Rooms per day · last 7 days (IST)</div>
              <div className="flex items-end gap-2 h-24" data-testid="rooms-daily-chart">
                {(() => {
                  const max = Math.max(1, ...roomsDaily.map((d) => d.count));
                  return roomsDaily.map((d) => {
                    const pct = (d.count / max) * 100;
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center justify-end min-w-0">
                        <div className="text-[9px] font-mono-arcade text-[#EAEB18] mb-1">{d.count}</div>
                        <div
                          className="w-full bg-[#00F0FF]/70 hover:bg-[#EAEB18] transition-colors"
                          style={{ height: `${pct}%`, minHeight: "2px" }}
                          title={`${d.date}: ${d.count}`}
                        />
                        <div className="text-[9px] uppercase tracking-widest text-[#94A3B8] mt-1">{d.day_label}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Rooms table */}
          <div className="overflow-x-auto border-t border-[#00F0FF]/20">
            <table className="w-full text-sm font-mono-arcade" data-testid="admin-rooms-table">
              <thead>
                <tr className="border-b border-[#00F0FF]/20 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Creator</th>
                  <th className="text-right p-3">Players</th>
                  <th className="text-right p-3">Scores</th>
                  <th className="text-right p-3">Top Score</th>
                  <th className="text-left p-3">Top Player</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Created (UTC)</th>
                </tr>
              </thead>
              <tbody>
                {rooms.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-xs text-[#94A3B8]">
                      No rooms {roomsFilter !== "all" ? `(${roomsFilter})` : ""} yet.
                    </td>
                  </tr>
                ) : (
                  rooms.map((r) => (
                    <tr key={r.room_id} className="border-b border-[#00F0FF]/10">
                      <td className="p-3 font-heading font-black tracking-widest text-[#EAEB18]">{r.room_code}</td>
                      <td className="p-3 text-white">{r.creator_name || "—"}</td>
                      <td className="p-3 text-right text-[#00F0FF]">{r.player_count}</td>
                      <td className="p-3 text-right text-[#94A3B8]">{r.score_count}</td>
                      <td className="p-3 text-right text-[#EAEB18]">{r.top_score ?? "—"}</td>
                      <td className="p-3 text-white">{r.top_player || "—"}</td>
                      <td className="p-3">
                        <span
                          className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                            r.status === "active"
                              ? "text-[#39FF14] border border-[#39FF14]/40"
                              : "text-[#94A3B8] border border-[#94A3B8]/40"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="p-3 text-[#94A3B8] text-xs">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Plan filter — applies to Top3, Daily/Weekly Reports, Winners */}
        <section
          data-testid="admin-plan-switch-section"
          className="mb-4 border border-[#EAEB18]/40 bg-[#0B0C10] px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2">
            <Trophy size={14} className="text-[#EAEB18]" />
            <div className="text-[11px] uppercase tracking-[0.25em] font-heading font-bold text-white">
              Leaderboard / Reports View
            </div>
          </div>
          <div className="flex gap-1" data-testid="admin-plan-switch">
            {[
              { key: "global", label: "Global", color: "#00F0FF" },
              { key: "darepass", label: "DarePass", color: "#EAEB18" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setReportsPlan(opt.key)}
                data-testid={`admin-plan-${opt.key}`}
                className={`text-[10px] uppercase tracking-[0.25em] font-heading font-black px-4 py-1.5 border transition-colors ${
                  reportsPlan === opt.key
                    ? "border-current bg-white/[0.03]"
                    : "border-white/20 text-[#94A3B8] hover:text-white"
                }`}
                style={{ color: reportsPlan === opt.key ? opt.color : undefined }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        {/* Top 3 */}
        <section className="mb-8 border border-[#EAEB18]/50 bg-[#0B0C10]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#EAEB18]/30 px-4 py-3">
            <div className="flex items-center gap-2">
              <Trophy size={16} strokeWidth={2.5} className="text-[#EAEB18]" />
              <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                Top 3 · Prize Pool
              </div>
            </div>
            <div className="flex gap-1">
              {["daily", "weekly"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  data-testid={`admin-top3-toggle-${p}`}
                  className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
                    period === p
                      ? "border-[#EAEB18] text-[#EAEB18] bg-[#EAEB18]/10"
                      : "border-[#00F0FF]/30 text-[#94A3B8] hover:text-[#00F0FF]"
                  }`}
                >
                  {p}
                </button>
              ))}
              <a
                href={`${API}/admin/export/leaderboard.csv?period=${period}&plan=${reportsPlan}&top=100`}
                data-testid="download-top100-csv"
                className="text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10"
                title="Download top 100 as CSV"
              >
                ⇩ CSV
              </a>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-mono-arcade" data-testid="admin-top3-table">
              <thead>
                <tr className="border-b border-[#00F0FF]/20 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                  <th className="text-left p-3">Rank</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-right p-3">Score</th>
                  <th className="text-left p-3">UPI</th>
                  <th className="text-right p-3">History</th>
                </tr>
              </thead>
              <tbody>
                {top3.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center p-6 text-[#94A3B8]">
                      No qualifying scores in this period.
                    </td>
                  </tr>
                )}
                {top3.map((row) => (
                  <tr
                    key={row.user_id}
                    className="border-b border-[#00F0FF]/10 hover:bg-[#EAEB18]/5 transition-colors"
                    data-testid={`admin-top3-row-${row.rank}`}
                  >
                    <td className="p-3 text-[#EAEB18] font-bold">#{row.rank}</td>
                    <td className="p-3 text-white">{row.name}</td>
                    <td className="p-3 text-[#94A3B8]">{row.email || "—"}</td>
                    <td className="p-3 text-right text-[#EAEB18] font-bold">
                      {String(row.score).padStart(5, "0")}
                    </td>
                    <td className="p-3">
                      {row.upi ? (
                        <span className="text-[#39FF14]">{row.upi}</span>
                      ) : (
                        <span className="text-[#FF007F] text-[10px] uppercase tracking-[0.2em]">
                          Awaiting UPI
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => openPlayer(row.user_id)}
                        data-testid={`admin-top3-history-btn-${row.rank}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:text-[#EAEB18] border border-[#00F0FF]/40 px-2 py-1"
                      >
                        <History size={12} /> Games
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* LEADERBOARD REPORTS: Last 7 days daily + Last 4 weeks weekly */}
        <section className="mb-8 border border-[#EAEB18]/40 bg-[#0B0C10]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#EAEB18]/30 px-4 py-3">
            <div className="flex items-center gap-2">
              <Calendar size={16} strokeWidth={2.5} className="text-[#EAEB18]" />
              <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                Leaderboard Reports
              </div>
              <span className="text-[9px] uppercase tracking-[0.2em] text-[#94A3B8]">
                · IST calendar
              </span>
            </div>
            <div className="flex gap-1 items-center">
              {[{k:"daily",l:"Last 7 Days"},{k:"weekly",l:"Last 4 Weeks"}].map((opt) => (
                <button
                  key={opt.k}
                  onClick={() => { setReportMode(opt.k); setExpandedReport(null); }}
                  data-testid={`admin-report-toggle-${opt.k}`}
                  className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
                    reportMode === opt.k
                      ? "border-[#EAEB18] text-[#EAEB18] bg-[#EAEB18]/10"
                      : "border-[#00F0FF]/30 text-[#94A3B8] hover:text-[#00F0FF]"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
              <button
                onClick={() => downloadReportsExcel(reportMode)}
                data-testid={`admin-report-download-${reportMode}`}
                title={`Download ${reportMode} reports as Excel`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10 ml-2"
              >
                <Download size={12} strokeWidth={2.5} /> XLSX
              </button>
            </div>
          </div>
          <div className="p-2 space-y-1">
            {(reportMode === "daily" ? dailyReports : weeklyReports).length === 0 && (
              <div className="text-center p-6 text-[#94A3B8] text-xs font-mono-arcade">
                No games in this range yet.
              </div>
            )}
            {(reportMode === "daily" ? dailyReports : weeklyReports).map((r) => {
              const key = r.date || r.week_start;
              const label = r.day_label || r.week_label;
              const isOpen = expandedReport === key;
              return (
                <div
                  key={key}
                  className="border border-[#00F0FF]/15 bg-[#050505]"
                  data-testid={`admin-report-${reportMode}-${key}`}
                >
                  <button
                    onClick={() => setExpandedReport(isOpen ? null : key)}
                    data-testid={`admin-report-expand-${reportMode}-${key}`}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#EAEB18]/5"
                  >
                    <div className="flex items-center gap-2 text-left">
                      {isOpen
                        ? <ChevronDown size={14} strokeWidth={2.5} className="text-[#EAEB18]" />
                        : <ChevronRight size={14} strokeWidth={2.5} className="text-[#00F0FF]" />}
                      <span className="font-heading font-bold text-xs uppercase tracking-widest text-white">
                        {label}
                      </span>
                      {r.top?.[0] && (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#EAEB18]">
                          · #1 {r.top[0].name} — {String(r.top[0].score).padStart(5, "0")}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                      {r.player_count} player{r.player_count !== 1 && "s"}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-[#00F0FF]/15 overflow-x-auto">
                      <table className="w-full text-xs font-mono-arcade">
                        <thead>
                          <tr className="border-b border-[#00F0FF]/10 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                            <th className="text-left p-2">Rank</th>
                            <th className="text-left p-2">Name</th>
                            <th className="text-left p-2">Email</th>
                            <th className="text-left p-2">UPI</th>
                            <th className="text-right p-2">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.top.length === 0 && (
                            <tr>
                              <td colSpan={5} className="text-center p-4 text-[#94A3B8]">
                                No games this {reportMode === "daily" ? "day" : "week"}.
                              </td>
                            </tr>
                          )}
                          {r.top.map((row) => (
                            <tr
                              key={row.user_id}
                              className="border-b border-[#00F0FF]/5 hover:bg-[#00F0FF]/5"
                            >
                              <td className="p-2 text-[#EAEB18] font-bold">
                                {row.rank === 1 ? "🏆 #1" : `#${row.rank}`}
                              </td>
                              <td className="p-2 text-white">
                                <button
                                  onClick={() => openPlayer(row.user_id)}
                                  data-testid={`admin-report-open-player-${key}-${row.user_id}`}
                                  className="hover:text-[#00F0FF] underline underline-offset-2"
                                >
                                  {row.name}
                                </button>
                              </td>
                              <td className="p-2 text-[#94A3B8]">{row.email || "—"}</td>
                              <td className="p-2 text-[#39FF14]">{row.upi || "—"}</td>
                              <td className="p-2 text-right text-[#EAEB18] font-bold">
                                {String(row.score).padStart(5, "0")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* WINNER CARDS: shareable posters for last 7 daily winners */}
        <section className="mb-8 border border-[#FF007F]/40 bg-[#0B0C10]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#FF007F]/30 px-4 py-3">
            <div className="flex items-center gap-2">
              <Share2 size={16} strokeWidth={2.5} className="text-[#FF007F]" />
              <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                Winner Cards
              </div>
              <span className="text-[9px] uppercase tracking-[0.2em] text-[#94A3B8]">
                · social share posters
              </span>
            </div>
            <a
              href={`${API}/admin/export/winners.csv?plan=${reportsPlan}&days=30`}
              data-testid="download-winners-csv"
              className="text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10"
              title="Download last 30 days winners as CSV"
            >
              ⇩ CSV · 30d
            </a>
          </div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {winnerCards.length === 0 && (
              <div className="col-span-full text-center p-6 text-[#94A3B8] text-xs font-mono-arcade">
                No games yet — winner cards will appear as players compete.
              </div>
            )}
            {winnerCards.map((w) => (
              <div
                key={w.date}
                className="border border-[#EAEB18]/30 bg-[#050505] p-3"
                data-testid={`admin-winner-card-${w.date}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#EAEB18]">
                    {w.day_label}
                  </div>
                  {w.is_today ? (
                    <span className="text-[9px] uppercase tracking-[0.2em] text-[#FF007F]">LIVE</span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-[0.2em] text-[#39FF14]">₹500</span>
                  )}
                </div>
                {w.winner ? (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      {w.winner.picture ? (
                        <img
                          src={w.winner.picture}
                          alt=""
                          className="w-9 h-9 border border-[#EAEB18]/40 object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-9 h-9 border border-[#EAEB18]/40 bg-[#0B0C10] flex items-center justify-center text-[#EAEB18] text-lg">🏆</div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-white text-sm font-mono-arcade truncate">
                          {w.winner.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-mono-arcade">
                          Score {String(w.winner.score).padStart(5, "0")}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        setActiveWinner({
                          winner: w.winner,
                          date: w.date,
                          dayLabel: w.day_label,
                        })
                      }
                      data-testid={`admin-winner-open-${w.date}`}
                      className="w-full inline-flex items-center justify-center gap-2 border border-[#EAEB18] text-[#EAEB18] text-[10px] uppercase tracking-[0.2em] py-2 hover:bg-[#EAEB18] hover:text-black transition-colors"
                    >
                      <Share2 size={12} strokeWidth={2.5} /> Open Card
                    </button>
                  </>
                ) : (
                  <div className="text-[10px] text-[#94A3B8] py-6 text-center uppercase tracking-[0.2em]">
                    No games this day
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Player history (recent games across all players) */}
        <section className="mb-8 border border-[#00F0FF]/30 bg-[#0B0C10]">
          <div className="border-b border-[#00F0FF]/30 px-4 py-3 flex items-center gap-2">
            <Gamepad2 size={16} strokeWidth={2.5} className="text-[#00F0FF]" />
            <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
              Recent Games (last 500)
            </div>
            {q && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                {filteredGames.length} match{filteredGames.length !== 1 && "es"}
              </div>
            )}
            <a
              href={`${API}/admin/export/games.csv?limit=5000`}
              data-testid="download-games-csv"
              className="ml-auto text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10"
              title="Download last 5000 games as CSV"
            >
              ⇩ CSV
            </a>
          </div>
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-xs font-mono-arcade" data-testid="admin-history-table">
              <thead className="sticky top-0 bg-[#0B0C10]">
                <tr className="border-b border-[#00F0FF]/20 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                  <th className="text-left p-3">Time</th>
                  <th className="text-left p-3">Player</th>
                  <th className="text-right p-3">Score</th>
                  <th className="text-right p-3">Duration</th>
                  <th className="text-right p-3">Review</th>
                </tr>
              </thead>
              <tbody>
                {filteredGames.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center p-6 text-[#94A3B8]">
                      {q ? "No matching games." : "No games yet."}
                    </td>
                  </tr>
                )}
                {filteredGames.map((g) => (
                  <tr key={g.game_id} className="border-b border-[#00F0FF]/10 hover:bg-[#00F0FF]/5">
                    <td className="p-3 text-[#94A3B8]">{formatTs(g.timestamp)}</td>
                    <td className="p-3 text-white">
                      <button
                        className="hover:text-[#00F0FF] underline underline-offset-2"
                        onClick={() => openPlayer(g.user_id)}
                        data-testid={`admin-history-open-player-${g.game_id}`}
                      >
                        {g.user_name}
                      </button>
                    </td>
                    <td className="p-3 text-right text-[#EAEB18]">
                      {String(g.score).padStart(5, "0")}
                    </td>
                    <td className="p-3 text-right text-[#00F0FF]">
                      {formatDuration(g.duration_seconds)}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => openReview(g.game_id)}
                        data-testid={`admin-review-btn-${g.game_id}`}
                        className="text-[10px] uppercase tracking-[0.2em] text-[#FF007F] hover:text-[#EAEB18] border border-[#FF007F]/40 px-2 py-1"
                      >
                        Verdict
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Players list */}
        <section className="border border-[#00F0FF]/30 bg-[#0B0C10]">
          <div className="border-b border-[#00F0FF]/30 px-4 py-3 flex items-center gap-2">
            <Users size={16} strokeWidth={2.5} className="text-[#00F0FF]" />
            <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
              All Players
            </div>
            {q && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                {filteredPlayers.length} match{filteredPlayers.length !== 1 && "es"}
              </div>
            )}
            <a
              href={`${API}/admin/export/players.csv`}
              data-testid="download-players-csv"
              className="ml-auto text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10"
              title="Download all players as CSV"
            >
              ⇩ CSV
            </a>
          </div>
          <div className="overflow-x-auto max-h-[300px]">
            <table className="w-full text-xs font-mono-arcade" data-testid="admin-players-table">
              <thead className="sticky top-0 bg-[#0B0C10]">
                <tr className="border-b border-[#00F0FF]/20 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">UPI</th>
                  <th className="text-left p-3">Joined</th>
                  <th className="text-right p-3">Last 50 Games</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlayers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center p-6 text-[#94A3B8]">
                      {q ? "No matching players." : "No players yet."}
                    </td>
                  </tr>
                )}
                {filteredPlayers.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#00F0FF]/10 hover:bg-[#00F0FF]/5">
                    <td className="p-3 text-white">{u.name}</td>
                    <td className="p-3 text-[#94A3B8]">{u.email}</td>
                    <td className="p-3 text-[#39FF14]">{u.upi || "—"}</td>
                    <td className="p-3 text-[#94A3B8]">{formatTs(u.created_at)}</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => openPlayer(u.user_id)}
                        data-testid={`admin-player-history-btn-${u.user_id}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:text-[#EAEB18] border border-[#00F0FF]/40 px-2 py-1"
                      >
                        <History size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {/* PLAYER GAMES MODAL */}
      {playerModal && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4"
          onClick={closePlayer}
          data-testid="admin-player-modal"
        >
          <div
            className="max-w-3xl w-full max-h-[85vh] overflow-hidden border-2 border-[#00F0FF] bg-[#0B0C10] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#00F0FF]/40 px-4 py-3">
              <div className="flex items-center gap-2">
                <History size={16} className="text-[#00F0FF]" strokeWidth={2.5} />
                <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                  {playerModal.user_name || "Player"} · Last 50 Games
                </div>
              </div>
              <button
                onClick={closePlayer}
                data-testid="admin-player-modal-close"
                className="text-[#FF007F] hover:text-[#EAEB18]"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            {playerLoading || playerModal.loading ? (
              <div className="p-8 text-center text-[#00F0FF] font-mono-arcade text-xs uppercase tracking-[0.3em] animate-pulse">
                Loading player history…
              </div>
            ) : playerModal.error ? (
              <div className="p-8 text-center text-[#FF007F] text-sm">{playerModal.error}</div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b border-[#00F0FF]/20 text-xs">
                  <MetaCell label="Email" value={playerModal.email || "—"} />
                  <MetaCell label="UPI" value={playerModal.upi || "—"} highlight={playerModal.upi ? "#39FF14" : "#94A3B8"} />
                  <MetaCell label="Personal Best" value={playerModal.personal_best ?? 0} highlight="#EAEB18" />
                  <MetaCell label="Total Games" value={playerModal.total_games ?? 0} highlight="#00F0FF" />
                </div>
                <div className="overflow-y-auto flex-1">
                  <table className="w-full text-xs font-mono-arcade">
                    <thead className="sticky top-0 bg-[#0B0C10]">
                      <tr className="border-b border-[#00F0FF]/20 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                        <th className="text-left p-3">#</th>
                        <th className="text-left p-3">Timestamp</th>
                        <th className="text-right p-3">Score</th>
                        <th className="text-right p-3">Duration</th>
                        <th className="text-right p-3">Flag</th>
                        <th className="text-right p-3">Review</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(playerModal.games || []).length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center p-6 text-[#94A3B8]">
                            No games recorded for this player.
                          </td>
                        </tr>
                      )}
                      {(playerModal.games || []).map((g, i) => (
                        <tr key={g.game_id} className="border-b border-[#00F0FF]/10 hover:bg-[#00F0FF]/5">
                          <td className="p-3 text-[#94A3B8]">{i + 1}</td>
                          <td className="p-3 text-white" data-testid={`player-game-ts-${g.game_id}`}>
                            {formatTs(g.timestamp)}
                          </td>
                          <td className="p-3 text-right text-[#EAEB18] font-bold" data-testid={`player-game-score-${g.game_id}`}>
                            {String(g.score).padStart(5, "0")}
                          </td>
                          <td className="p-3 text-right text-[#00F0FF]">
                            {formatDuration(g.duration_seconds)}
                          </td>
                          <td className="p-3 text-right">
                            {g.capped_at_max ? (
                              <span className="text-[#FF007F] text-[10px] uppercase tracking-[0.2em]">CAPPED</span>
                            ) : (
                              <span className="text-[#39FF14] text-[10px]">✓</span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <button
                              onClick={() => openReview(g.game_id)}
                              data-testid={`player-game-review-${g.game_id}`}
                              className="text-[10px] uppercase tracking-[0.2em] text-[#FF007F] hover:text-[#EAEB18] border border-[#FF007F]/40 px-2 py-1"
                            >
                              Verdict
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* GAME REVIEW MODAL (bot verdict) */}
      {reviewData && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur flex items-center justify-center p-4"
          onClick={closeReview}
          data-testid="admin-review-modal"
        >
          <div
            className="max-w-lg w-full border-2 border-[#FF007F] bg-[#0B0C10] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                Bot-Detection Verdict
              </div>
              <button
                onClick={closeReview}
                data-testid="admin-review-modal-close"
                className="text-[#FF007F] hover:text-[#EAEB18]"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            {reviewLoading || reviewData.loading ? (
              <div className="text-[#00F0FF] font-mono-arcade text-xs uppercase tracking-[0.3em] animate-pulse py-6 text-center">
                Analyzing input timeline…
              </div>
            ) : reviewData.error ? (
              <div className="text-[#FF007F] text-sm">{reviewData.error}</div>
            ) : (
              <>
                <div className="border border-[#00F0FF]/30 p-4 mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    {(() => {
                      const Icon = verdictIcon(reviewData.verdict);
                      return (
                        <Icon
                          size={24}
                          strokeWidth={2.5}
                          style={{ color: verdictColor(reviewData.verdict) }}
                        />
                      );
                    })()}
                    <div
                      className="font-heading font-black text-xl tracking-wider"
                      style={{ color: verdictColor(reviewData.verdict) }}
                      data-testid="admin-review-verdict"
                    >
                      {reviewData.verdict}
                    </div>
                  </div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{reviewData.reason}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                  <MetaCell label="Player" value={reviewData.user_name} />
                  <MetaCell label="Score" value={reviewData.score} highlight="#EAEB18" />
                  <MetaCell label="Duration" value={formatDuration(reviewData.duration_seconds)} highlight="#00F0FF" />
                  <MetaCell label="Timestamp" value={formatTs(reviewData.timestamp)} />
                  <MetaCell label="Events" value={reviewData?.stats?.event_count ?? 0} />
                  <MetaCell label="Avg Δ" value={`${reviewData?.stats?.avg_interval_ms ?? 0}ms`} />
                  <MetaCell label="Min Δ" value={`${reviewData?.stats?.min_interval_ms ?? 0}ms`} />
                  <MetaCell label="Std Δ" value={`${reviewData?.stats?.std_interval_ms ?? 0}ms`} highlight="#FF007F" />
                </div>

                {reviewData.capped_at_max && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF007F] border border-[#FF007F]/40 px-2 py-1 inline-block">
                    ⚠ Score hit hard cap
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* WINNER CARD MODAL */}
      {activeWinner && (
        <WinnerCard
          winner={activeWinner.winner}
          date={activeWinner.date}
          dayLabel={activeWinner.dayLabel}
          onClose={() => setActiveWinner(null)}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div
      className="border border-[#00F0FF]/30 bg-[#0B0C10] p-4"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <Icon size={18} strokeWidth={2.5} style={{ color }} />
        <div className="text-[9px] uppercase tracking-[0.3em] text-[#94A3B8]">{label}</div>
      </div>
      <div className="font-mono-arcade font-bold text-2xl text-white">{value}</div>
    </div>
  );
}

function MetaCell({ label, value, highlight = "#FFFFFF" }) {
  return (
    <div className="border border-[#00F0FF]/20 bg-[#050505] px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#94A3B8] mb-1">{label}</div>
      <div className="font-mono-arcade text-sm" style={{ color: highlight }}>
        {value}
      </div>
    </div>
  );
}
